import { EntityStore } from '../../../shared/stores/EntityStore';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { api } from '../../../shared/api/api';

export class JobPositionStore extends EntityStore {
	constructor() {
		super('jobs');
		makeObservable(this);
	}

	@observable jobPositions = [];

	statusOptions = [
		{
			text: 'Open',
			value: 'open',
			colors: {
				text: '#0EA688',
				border: '#C1F5EAD4'
			}
		},
		{
			text: 'Draft',
			value: 'draft',
			colors: {
				text: '#6938EF',
				border: '#EBE9FE'
			}
		},
		{
			text: 'On-Hold',
			value: 'on_hold',
			colors: {
				text: '#F79009',
				border: '#FEF0C7'
			}
		},
		{
			text: 'Closed',
			value: 'closed',
			colors: {
				text: '#717680',
				border: '#F5F5F5'
			}
		}
	];

	publishOptions = [
		{
			text: 'Unpublished',
			value: true
		},
		{
			text: 'Published',
			value: false
		}
	];

	priorityOptions = [
		{
			text: '1',
			value: '1'
		},
		{
			text: '2',
			value: '2'
		},
		{
			text: '3',
			value: '3'
		},
		{
			text: '4',
			value: '4'
		}
	];

	urgencyOptions = [
		{
			text: 'Critical',
			value: 'critical'
		},
		{
			text: 'High',
			value: 'high'
		},
		{
			text: 'Follow Up',
			value: 'follow_up'
		},
		{
			text: 'Proactive',
			value: 'proactive'
		}
	];

	workScheduleOptions = [
		{
			label: 'Full-time',
			text: 'Full-time',
			value: 'full_time'
		},
		{
			label: 'Part-time',
			text: 'Part-time',
			value: 'part_time'
		},
		{
			label: 'Flexible',
			text: 'Flexible',
			value: 'flexible'
		}
	];

	termOptions = [
		{
			label: 'Permanent',
			text: 'Permanent',
			value: 'permanent'
		},
		{
			label: 'Long Term',
			text: 'Long Term',
			value: 'long_term'
		},
		{
			label: 'Short Term',
			text: 'Short Term',
			value: 'short_term'
		},
		{
			label: 'Temporary',
			text: 'Temporary',
			value: 'temporary'
		}
	];

	locationTypeOptions = [
		{
			label: 'Remote',
			text: 'Remote',
			value: 'remote'
		},
		{
			label: 'On-site',
			text: 'On-site',
			value: 'on_site'
		},
		{
			label: 'Hybrid',
			text: 'Hybrid',
			value: 'hybrid'
		}
	];

	defaultTableColumnsOptions = [
		{ label: 'ID', value: 'id' },
		{ label: 'Priority', value: 'priority' },
		{ label: 'Urgency', value: 'urgency' },
		{ label: 'Job Title', value: 'title' },
		{ label: 'Client', value: 'client' },
		{ label: 'Owner', value: 'owner' },
		{ label: 'Account Manager', value: 'account_manager' },
		{ label: 'Status', value: 'status' },
		{ label: 'Shortlisted Candidates', value: 'shortlisted_candidates' }
	];

	tableColumnsOptions = [
		{ label: 'ID', value: 'id' },
		{ label: 'Priority', value: 'priority' },
		{ label: 'Urgency', value: 'urgency' },
		{ label: 'Job Title', value: 'title' },
		{ label: 'Client', value: 'client' },
		{ label: 'Owner', value: 'owner' },
		{ label: 'Account Manager', value: 'account_manager' },
		{ label: 'Status', value: 'status' },
		{ label: 'Shortlisted Candidates', value: 'shortlisted_candidates' },
		{ label: 'Role', value: 'role' },
		{ label: 'Hiring Manager', value: 'hiring_manager' },
		{ label: 'Total Vacancies', value: 'total_vacancies' },
		{ label: 'Created On', value: 'created_on' },
		{ label: 'Updated On', value: 'updated_on' },
		{ label: 'Locations', value: 'locations' },
		{ label: 'Vacancies Days Open Avg', value: 'vacancies_days_open_avg' }
	];

	getUrgencyByValue = value => {
		return this.urgencyOptions.find(urgency => urgency.value === value);
	};

	getStatusByValue = value => {
		return this.statusOptions.find(status => status.value === value);
	};

	@action
	async loadJobPositions(reset = false) {
		const result = await this.search(reset);
		runInAction(() => {
			this.jobPositions = result.data.rows;
		});
	}

	@action
	async getJobs(query = {}) {
		const pagination =
			query && query.pagination
				? query.pagination
				: {
						page: 1,
						limit: 20
					};

		const result = await api.post(`${this.entityPluralEndpoint}/query`, {
			query: {
				...query,
				pagination
			}
		});

		return result;
	}

	@action
	async searchPublic(query = {}) {
		const result = await api.post('public/jobs/query', { query });
		return result;
	}

	@action
	async getPublic(id) {
		const result = await api.get(`public/jobs/${id}`);
		return result;
	}

	@action
	async createPublicExternalApplication(jobId, values) {
		const formData = new FormData();
		formData.append('name', values.name);
		formData.append('email', values.email);
		formData.append('linkedinUrl', values.linkedinUrl);
		formData.append('source', values.source);
		formData.append('englishLevel', values.englishLevel);
		if (values.countryId) {
			formData.append('countryId', values.countryId);
		}
		if (values.resume) {
			formData.append('resume', values.resume);
		}
		if (values.referralToken) {
			formData.append('referralToken', values.referralToken);
		}

		return api.post(`public/jobs/${jobId}/external-applications`, formData, {
			headers: {
				'content-type': 'multipart/form-data'
			}
		});
	}

	@action
	async createReferral(jobId, values) {
		return api.post('job-referrals', {
			jobId,
			name: values.name,
			email: values.email,
			linkedinUrl: values.linkedinUrl,
			comments: values.comments
		});
	}

	@action
	async getPublicReferral(token, jobId) {
		return api.get(`public/job-referrals/${encodeURIComponent(token)}`, {
			params: { jobId }
		});
	}

	@action
	async getAllAuth(applicantId) {
		const result = await api.get(`${this.entityPluralEndpoint}-auth`, {
			params: { applicantId }
		});
		return result;
	}

	@action
	async getAuth(jobId) {
		const result = await api.get(`${this.entityPluralEndpoint}-auth/${jobId}`);
		return result;
	}

	@action
	async addApplicant(id, request) {
		const result = await api.post(
			`${this.entityPluralEndpoint}/${id}/applicant`,
			request
		);
		return result;
	}

	@action
	async addApplicants(id, request) {
		const result = await api.post(
			`${this.entityPluralEndpoint}/${id}/applicants`,
			request
		);
		return result;
	}

	@action
	async deleteApplicant(id, applicantId) {
		const result = await api.delete(
			`${this.entityPluralEndpoint}/${id}/applicants/${applicantId}`
		);
		return result;
	}

	@action
	async duplicate(id) {
		const result = await api.post(
			`${this.entityPluralEndpoint}/${id}/duplicate`
		);
		return result;
	}

	@action
	async bulkUpdate(ids, job) {
		const result = await api.post(`${this.entityPluralEndpoint}/bulk/update`, {
			ids,
			job
		});
		return result;
	}

	@action
	async bulkDelete(ids) {
		const result = await api.post(`${this.entityPluralEndpoint}/bulk/delete`, {
			ids
		});
		return result;
	}
}

export const jobPositionStore = new JobPositionStore();
